import { useDictionaryContext } from 'application/contexts/DictionaryContext';
import { getJobs } from 'application/repositories/jobRepository';
import { ListFilters } from 'designsystem/components/2-molecules/ListFilters';
import { CatalogPage } from 'designsystem/components/5-templates/CatalogPage';
import { ModuleFeature } from 'features/Modules/DynamicModule';
import { LinkFeature } from 'features/Modules/Link';
import { TopBannerFeature } from 'features/Modules/TopBanner';
import { useEffect, useState } from 'react';

export interface JobListItemType {
	heading: string;
	link: React.ReactElement;
	teaser?: string;
	additionalInfo?: {
		date: string;
		location: string;
	};
	location?: string;
	category?: string;
}

export const JobCatalogPageFeature: React.FC<Content.JobCatalogPage> = ({ properties, additionalTop, additionalPageContent }) => {
	const { heading, teaser, department, traineesOption, catalogContent, bannerContent } = properties ?? {};
	const dictionary = useDictionaryContext();

	const [filters, setFilters] = useState(null);
	const [jobCatalogResult, setJobCatalogResult] = useState<JobListItemType[]>(null);
	const [jobCatalogLocations, setJobCatalogLocations] = useState<string[]>(null);
	const [jobCatalogCategories, setJobCatalogCategories] = useState<string[]>(null);
	const [filteredList, setFilteredList] = useState(jobCatalogResult);

	useEffect(() => {
		const get = async () => {
			const jobCatalogData = await getJobs(department, traineesOption);

			setJobCatalogLocations(jobCatalogData.locations);
			setJobCatalogCategories(jobCatalogData.categories);
			setJobCatalogResult(
				jobCatalogData.jobs.map((job) => {
					return {
						...job,
						link: <LinkFeature {...job.link} target="_blank" />,
					};
				}),
			);
		};
		get();
	}, [department, traineesOption]);

	useEffect(() => {
		const selectedFilters = filters?.map((tag) => tag.value);
		let catalogList = jobCatalogResult;
		if (selectedFilters?.some((tag) => jobCatalogCategories.includes(tag))) {
			catalogList = catalogList.filter((catalogListItem) => {
				return selectedFilters.includes(catalogListItem.category);
			});
		}
		if (selectedFilters?.some((tag) => jobCatalogLocations.includes(tag))) {
			catalogList = catalogList.filter((catalogListItem) => {
				return selectedFilters.includes(catalogListItem.location);
			});
		}
		setFilteredList(catalogList);
	}, [filters, jobCatalogCategories, jobCatalogLocations, jobCatalogResult]);

	return (
		<CatalogPage
			additionalTop={additionalTop}
			additionalPageContent={additionalPageContent}
			heading={heading}
			teaser={teaser}
			noItemsMessage={dictionary.getServiceInfo('NoJobs')}
			filters={
				<ListFilters
					filters={[
						{ groupId: 'area', label: dictionary.getLabel('Area'), options: jobCatalogLocations },
						{ groupId: 'jobCategory', label: dictionary.getLabel('JobCategory'), options: jobCatalogCategories },
					]}
					removeFilter={dictionary.getAction('RemoveFilter')}
					choosenDropdownFilters={(filters) => setFilters(filters)}
					historicCheckboxLabel={dictionary.getLabel('HistoricalArchive')}
				/>
			}
			catalogList={filteredList}
			pageSizeOptions={[10, 20]}
			pageSizeAllOption={dictionary.getLabel('All')}
			pageSizeOptionsDefaultValueIndex={1}
			paginationErrorText={dictionary.getError('CatalogPagination')}
			paginationTexts={{
				totalResultsText: dictionary.getLabel('TotalResults'),
				showText: dictionary.getAction('Show').toLowerCase(),
				resultsPerPageText: dictionary.getLabel('ResultsPerPage').toLowerCase(),
				pageText: dictionary.getLabel('Page'),
				outOfText: dictionary.getLabel('OutOf').toLowerCase(),
				pagesText: dictionary.getLabel('Pages').toLowerCase(),
			}}
			paginationAriaLabels={{
				selectInputAriaLabel: dictionary.getLabel('NumberOfResultsPerPage'),
				textInputAriaLabel: dictionary.getLabel('CurrentPage'),
				prevButtonAriaLabel: dictionary.getLabel('PreviousPage'),
				nextButtonAriaLabel: dictionary.getLabel('NextPage'),
			}}
			topBanner={bannerContent?.items?.length > 0 && <TopBannerFeature bannerContent={bannerContent} />}
			catalogContent={catalogContent?.items.map((content, key) => (
				<ModuleFeature contentModule={content} key={key} />
			))}
			loadingText={dictionary.getServiceInfo('FetchingData')}
		/>
	);
};
